import { useState, useEffect, useCallback } from 'react'
import { getCitatorCaseCitations } from '../fetch/get-citator-case-citations'
import { CitatorCitingCasesResponse } from '../schema/case-review-schema'

interface UseGetCitatorCaseCitationsState {
  data: CitatorCitingCasesResponse | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

/**
 * Use Get Citator Case Citations
 * Fetches case citations for a given parent_id.
 *
 * @param parent_id - The parent ID of the case
 * @returns data, isLoading, isError, and refetch function
 */
export function useGetCitatorCaseCitations(parent_id: string): UseGetCitatorCaseCitationsState {
  const [data, setData] = useState<CitatorCitingCasesResponse | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  const fetchData = useCallback(async () => {
    if (!parent_id) {
      setData(undefined)
      setIsError(true)
      return
    }

    setIsLoading(true)
    setIsError(false)

    try {
      const result = await getCitatorCaseCitations(parent_id)

      if (!result) {
        setIsError(true)
        setData(undefined)
      } else {
        setData(result)
        setIsError(false)
      }
    } catch (err) {
      setIsError(true)
      setData(undefined)
      console.error('Error fetching case citations:', err)
    } finally {
      setIsLoading(false)
    }
  }, [parent_id])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const refetch = useCallback(() => {
    fetchData()
  }, [fetchData])

  return {
    data,
    isLoading,
    isError,
    refetch,
  }
}
